import apiClient, { API_ENDPOINTS } from "./config";
import { User } from "../types/userTypes";
import {
  RegisterRequest,
  LoginResponseData, // Contains user and tokens (this is the nested 'data' part for login)
  // RegisterResponseData, // This was for the overall response, BackendResponse will handle structure
} from "../types/authTypes";
import { BackendResponse } from "../types/apiTypes"; // Use the new BackendResponse

// Specific payload types for BackendResponse data field
interface RefreshTokenData {
  accessToken: string;
  refreshToken?: string;
}

// Type for the data field in the register response (if user is returned)
// Backend returns user directly at top level, not in 'data' for register
// So BackendResponse<null, User> is more appropriate for register.

export const authApi = {
  register: async (
    userData: RegisterRequest
  ): Promise<BackendResponse<null, User>> => {
    // TData is null, TUser is User
    const response = await apiClient.post<BackendResponse<null, User>>(
      API_ENDPOINTS.AUTH_REGISTER,
      userData
    );
    return response.data; // The whole response object { status, message, user?, requiresVerification? }
  },

  login: async (credentials: {
    email: string;
    password: string;
  }): Promise<BackendResponse<LoginResponseData>> => {
    // TData is LoginResponseData
    const response = await apiClient.post<BackendResponse<LoginResponseData>>(
      API_ENDPOINTS.AUTH_LOGIN,
      credentials
    );
    return response.data; // The whole response { status, message, data: { user, tokens } }
  },

  logout: async (): Promise<BackendResponse> => {
    // TData is null by default
    const response = await apiClient.post<BackendResponse>(
      API_ENDPOINTS.AUTH_LOGOUT
    );
    return response.data;
  },

  verifyEmail: async (data: {
    email: string;
    code: string;
  }): Promise<BackendResponse> => {
    const response = await apiClient.post<BackendResponse>(
      API_ENDPOINTS.AUTH_VERIFY_EMAIL,
      data
    );
    return response.data;
  },

  resendVerificationCode: async (data: {
    email: string;
  }): Promise<BackendResponse> => {
    const response = await apiClient.post<BackendResponse>(
      API_ENDPOINTS.AUTH_RESEND_VERIFICATION,
      data
    );
    return response.data;
  },

  forgotPassword: async (data: { email: string }): Promise<BackendResponse> => {
    const response = await apiClient.post<BackendResponse>(
      API_ENDPOINTS.AUTH_FORGOT_PASSWORD,
      data
    );
    return response.data;
  },

  resetPassword: async (data: {
    email: string;
    code: string;
    newPassword: string;
  }): Promise<BackendResponse> => {
    const response = await apiClient.post<BackendResponse>(
      API_ENDPOINTS.AUTH_RESET_PASSWORD,
      data
    );
    return response.data;
  },

  refreshToken: async (data: {
    refreshToken: string;
  }): Promise<BackendResponse<RefreshTokenData>> => {
    // TData is RefreshTokenData
    const response = await apiClient.post<BackendResponse<RefreshTokenData>>(
      API_ENDPOINTS.AUTH_REFRESH_TOKEN,
      data
    );
    return response.data; // The whole response { status, data: { accessToken, refreshToken? } }
  },

  getProfile: async (
    userId: string
  ): Promise<BackendResponse<{ user: User }>> => {
    // Assuming profile returns data nested
    const response = await apiClient.get<BackendResponse<{ user: User }>>(
      API_ENDPOINTS.USER_PROFILE(userId)
    );
    return response.data;
  },

  googleLogin: async (
    data: { code: string }
  ): Promise<BackendResponse<LoginResponseData>> => {
    const response = await apiClient.post<BackendResponse<LoginResponseData>>(
      API_ENDPOINTS.AUTH_GOOGLE,
      data
    );
    return response.data;
  },
};
